import { useEffect, useState } from 'react';

const PINCODE_API_BASE = 'https://api.pincodeapi.in/api/v1/pincode';

const getPostOffices = (payload) => {
  const data = payload?.data ?? payload;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.post_offices)) return data.post_offices;
  if (Array.isArray(data?.PostOffice)) return data.PostOffice;
  return [];
};

const getValue = (record, keys) => {
  for (const key of keys) {
    const value = record?.[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
};

const normalizePincodeLocation = (payload) => {
  const offices = getPostOffices(payload);
  const record = offices.find((office) =>
    getValue(office, ['state', 'statename', 'state_name'])
  );

  if (!record) return null;

  // The API's current schema exposes district rather than a separate city.
  const city = getValue(record, ['city', 'city_name', 'town', 'district', 'district_name']);
  const state = getValue(record, ['state', 'statename', 'state_name']);
  return city && state ? { city, state } : null;
};

/** Fetch city/state suggestions from the postal directory, independently of delivery serviceability. */
const usePincodeLocation = (pincode) => {
  const [location, setLocation] = useState(null);

  useEffect(() => {
    const pin = String(pincode || '').trim();
    setLocation(null);
    if (!/^\d{6}$/.test(pin)) return undefined;

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const response = await fetch(`${PINCODE_API_BASE}/${pin}`, {
          method: 'GET',
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });
        if (!response.ok) return;

        const payload = await response.json();
        if (payload?.success === false) return;
        const result = normalizePincodeLocation(payload);
        if (result && !controller.signal.aborted) {
          setLocation({ ...result, pincode: pin });
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          // Address entry can continue if the optional location lookup is unavailable.
        }
      }
    }, 350);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [pincode]);

  return location;
};

export default usePincodeLocation;
